// This package has zero dependencies, so day and week boundaries are computed
// with the runtime's own Intl and never with a date library. The locale
// literal is always "en-US". A week is seven calendar days, never
// 7 * 86_400_000, because that is wrong across a DST change.

/** "YYYY-MM-DD". */
export type ProviderDay = string;

type typeCivilParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const pad2 = ({ value }: { value: number }): string =>
  String(value).padStart(2, "0");

const pad4 = ({ value }: { value: number }): string =>
  String(value).padStart(4, "0");

const parseDay = ({
  day,
}: {
  day: ProviderDay;
}): { year: number; month: number; day: number } => {
  const [yearText, monthText, dayText] = day.split("-");
  return {
    year: Number(yearText),
    month: Number(monthText),
    day: Number(dayText),
  };
};

const partText = ({
  parts,
  type,
}: {
  parts: Intl.DateTimeFormatPart[];
  type: Intl.DateTimeFormatPartTypes;
}): string | undefined => parts.find((part) => part.type === type)?.value;

export const civilPartsFromTexts = ({
  yearText,
  monthText,
  dayText,
  hourText,
  minuteText,
  secondText,
  timeZone,
}: {
  yearText: string | undefined;
  monthText: string | undefined;
  dayText: string | undefined;
  hourText: string | undefined;
  minuteText: string | undefined;
  secondText: string | undefined;
  timeZone: string;
}): typeCivilParts => {
  if (
    yearText === undefined ||
    monthText === undefined ||
    dayText === undefined ||
    hourText === undefined ||
    minuteText === undefined ||
    secondText === undefined
  ) {
    throw new Error(`could not parse civil parts for ${timeZone}`);
  }
  return {
    year: Number(yearText),
    month: Number(monthText),
    day: Number(dayText),
    hour: Number(hourText),
    minute: Number(minuteText),
    second: Number(secondText),
  };
};

// ISSUE-1062. One formatter per zone, never one per call. On Bun 1.3.11, the
// runtime every service image runs, each new Intl.DateTimeFormat keeps about
// 7 MiB of RSS per 1,000 after GC, and providerDayToInstant alone builds three
// or four. contest-api-jobs runs these on every round and window check, and
// was OOMKilled at its 768Mi limit about every 17 hours. A formatter is
// immutable, so sharing it changes no result. Intl reads a zone name in any
// letter case, so the key is the lowercased name: case variants of one zone
// share one formatter instead of each taking a slot. The cap bounds the map
// when a zone string comes from outside; past it a call still gets a
// correct, unshared formatter.
const CIVIL_FORMATTER_CACHE_MAX_ENTRIES = 512;
const civilFormatterByTimeZone = new Map<string, Intl.DateTimeFormat>();

/** Throws the constructor's RangeError for an unknown zone; never caches one. */
const civilFormatter = ({
  timeZone,
}: {
  timeZone: string;
}): Intl.DateTimeFormat => {
  const cacheKey = timeZone.toLowerCase();
  const cached = civilFormatterByTimeZone.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const created = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  if (civilFormatterByTimeZone.size < CIVIL_FORMATTER_CACHE_MAX_ENTRIES) {
    civilFormatterByTimeZone.set(cacheKey, created);
  }
  return created;
};

const readCivilParts = ({
  instantMs,
  timeZone,
}: {
  instantMs: number;
  timeZone: string;
}): typeCivilParts => {
  const parts = civilFormatter({ timeZone }).formatToParts(new Date(instantMs));
  return civilPartsFromTexts({
    yearText: partText({ parts, type: "year" }),
    monthText: partText({ parts, type: "month" }),
    dayText: partText({ parts, type: "day" }),
    hourText: partText({ parts, type: "hour" }),
    minuteText: partText({ parts, type: "minute" }),
    secondText: partText({ parts, type: "second" }),
    timeZone,
  });
};

/** True when Intl recognizes the string as an IANA zone. Never throws. */
export const isValidTimeZone = ({
  timeZone,
}: {
  timeZone: string;
}): boolean => {
  if (timeZone === "") {
    return false;
  }
  // Shares the civil formatter's cache rather than building one per check.
  try {
    civilFormatter({ timeZone });
    return true;
  } catch {
    return false;
  }
};

/** Milliseconds to ADD to an instant to reach the wall-clock reading in `timeZone`.
 *  America/New_York in winter returns -18_000_000. */
export const offsetMsAt = ({
  instantMs,
  timeZone,
}: {
  instantMs: number;
  timeZone: string;
}): number => {
  const { year, month, day, hour, minute, second } = readCivilParts({
    instantMs,
    timeZone,
  });
  return Date.UTC(year, month - 1, day, hour, minute, second) - instantMs;
};

/** The calendar day `instantMs` falls on in `timeZone`. "YYYY-MM-DD". */
export const formatProviderDay = ({
  instantMs,
  timeZone,
}: {
  instantMs: number;
  timeZone: string;
}): ProviderDay => {
  const { year, month, day } = readCivilParts({ instantMs, timeZone });
  return `${pad4({ value: year })}-${pad2({ value: month })}-${pad2({ value: day })}`;
};

/** The absolute instant of 00:00 on `day` in `timeZone`. Earliest valid
 *  candidate, or inside a spring-forward gap where midnight does not exist,
 *  the largest candidate, the first instant that day does exist. Never throws
 *  for a gap. */
export const providerDayToInstant = ({
  day,
  timeZone,
}: {
  day: ProviderDay;
  timeZone: string;
}): number => {
  const { year, month, day: dayOfMonth } = parseDay({ day });
  const guess = Date.UTC(year, month - 1, dayOfMonth);
  const o1 = offsetMsAt({ instantMs: guess, timeZone });
  const t1 = guess - o1;
  const o2 = offsetMsAt({ instantMs: t1, timeZone });
  const t2 = guess - o2;
  const candidates = t1 === t2 ? [t1] : [t1, t2];
  const valid = candidates.filter(
    (t) => t + offsetMsAt({ instantMs: t, timeZone }) === guess,
  );
  return valid.length > 0 ? Math.min(...valid) : Math.max(...candidates);
};

/** Civil calendar arithmetic on a "YYYY-MM-DD" string. No timezone involved. */
export const addProviderDays = ({
  day,
  days,
}: {
  day: ProviderDay;
  days: number;
}): ProviderDay => {
  const { year, month, day: dayOfMonth } = parseDay({ day });
  const epochDay = Date.UTC(year, month - 1, dayOfMonth) / 86_400_000;
  return new Date((epochDay + days) * 86_400_000).toISOString().slice(0, 10);
};

/** ISO-8601 weekday of a "YYYY-MM-DD" string: 1 = Monday … 7 = Sunday. */
export const providerDayIsoWeekday = ({
  day,
}: {
  day: ProviderDay;
}): number => {
  const { year, month, day: dayOfMonth } = parseDay({ day });
  const weekday = new Date(Date.UTC(year, month - 1, dayOfMonth)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
};
